// source code from: https://www.npmjs.com/package/public-google-sheets-parser
// adjusted to also apply headers to rows where the row has empty values

type Option = {
  sheetName?: string;
  sheetId?: string;
  useFormat?: boolean;
  useFormattedDate?: boolean;
};

export class PublicGoogleSheetsParser {
  private id: string | undefined;
  private sheetName: string | undefined | null;
  private sheetId: string | undefined | null;
  private useFormat: boolean | undefined | null;
  private useFormattedDate: boolean | undefined | null;

  constructor(spreadsheetId: string, option: Option) {
    this.id = spreadsheetId;
    this.setOption(option);
  }

  setOption(option: Option) {
    if (!option) {
      this.sheetName = this.sheetName || null;
      this.sheetId = this.sheetId || null;
      this.useFormattedDate = this.useFormattedDate || false;
      this.useFormat = this.useFormat || false;
    } else if (typeof option === 'string') {
      this.sheetName = option;
      this.sheetId = this.sheetId || null;
    } else if (typeof option === 'object') {
      this.sheetName = option.sheetName || this.sheetName;
      this.sheetId = option.sheetId || this.sheetId;
      this.useFormattedDate =
        typeof option.useFormattedDate === 'boolean'
          ? option.useFormattedDate
          : this.useFormattedDate;
      this.useFormat =
        typeof option.useFormat === 'boolean'
          ? option.useFormat
          : this.useFormat;
    }
  }

  isDate(date: string) {
    return (
      date && typeof date === 'string' && /Date\((\d+),(\d+),(\d+)\)/.test(date)
    );
  }

  async getSpreadsheetDataUsingFetch() {
    if (!this.id) return null;

    let url = `https://docs.google.com/spreadsheets/d/${this.id}/gviz/tq?`;
    url += this.sheetId ? `gid=${this.sheetId}` : `sheet=${this.sheetName}`;

    try {
      const response = await fetch(url);
      return response?.ok ? response.text() : null;
    } catch (e) {
      /* istanbul ignore next */
      console.error('Error fetching spreadsheet data:', e);
      /* istanbul ignore next */
      return null;
    }
  }

  normalizeRow(rows: any[]) {
    return rows.map((row) =>
      row && row.v !== null && row.v !== undefined ? row : {}
    );
  }

  applyHeaderIntoRows(header: string[], rows: { c: { v: string }[] }[]) {
    return rows
      .map(({ c: row }) => this.normalizeRow(row))
      .map((row) =>
        row.reduce(
          (
            acc: Record<string, string>,
            column: { v: string; f?: string },
            index: number
          ) => {
            const value = column.v ?? '';

            if (!header[index]) return acc;

            return Object.assign(acc, {
              [header[index]]: this.useFormat
                ? column.f || value
                : this.useFormattedDate && this.isDate(value)
                  ? column.f || value
                  : value,
            });
          },
          {}
        )
      );
  }

  getItems(spreadsheetResponse: string) {
    let rows: { c: { v: string }[] }[] = [];

    try {
      const payloadExtractRegex =
        /google\.visualization\.Query\.setResponse\(({.*})\);/;
      const [_, payload] = spreadsheetResponse.match(payloadExtractRegex) ?? [];
      const parsedJSON = JSON.parse(payload) as {
        table: {
          cols: { label: string; id: string; type: 'string' }[];
          rows: { c: { v: string }[] }[];
        };
      };
      const hasSomeLabelPropertyInCols = parsedJSON.table.cols.some(
        ({ label }) => !!label
      );

      if (hasSomeLabelPropertyInCols) {
        const header = parsedJSON.table.cols.map(({ label }) => label);

        rows = this.applyHeaderIntoRows(header, parsedJSON.table.rows);
      } else {
        const [h, ...originalRows] = parsedJSON.table.rows;
        const headerRow = h.c.filter((x) => x?.v);
        const header = this.normalizeRow(headerRow).map((row) => row.v);

        rows = this.applyHeaderIntoRows(header, originalRows);
      }
    } catch (e) {
      /* istanbul ignore next */
      console.error('Error parsing spreadsheet data:', e);
    }

    return rows;
  }

  async parse(spreadsheetId?: string, option?: Option) {
    if (spreadsheetId) this.id = spreadsheetId;
    if (option) this.setOption(option);

    if (!this.id) throw new Error('SpreadsheetId is required.');

    const spreadsheetResponse = await this.getSpreadsheetDataUsingFetch();

    if (spreadsheetResponse === null) return [];

    return this.getItems(spreadsheetResponse);
  }
}
